import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import AsyncSelect from 'react-select/async'
import Modal from '~/prix/react/components/modal'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import useAPI from '~/prix/react/hooks/api'
import geoListDefinition from '~/packages/geo/list/geoList.definition'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
  spaceTypeSingularLabels,
} from '~/packages/general/variable/spaceTime.data'
import {
  Layer,
  Space,
  SpaceChildrenGeoStandard,
  SpaceChildrenItem,
  SpaceGeoStandardId,
  SpaceGeoStandardType,
  SpaceType,
} from '~/packages/general/variable/spaceTime.types'
import debounce from 'debounce-promise'
import DemarcationIcon from '~/components/icons/design/64px_draw-polygon.svg'
import ModalSpaceDemarcation, {
  FeaturePolygonWithHash,
} from '~/components/mapContent/modals/modalSpaceDemarcation'
import { VariableAndOptionalResult } from '~/packages/general/variable/variables.typing'
import {
  BaseWrapper,
  ChildrenOptions,
  ChildrenOption,
  Left,
  Title,
} from '~/components/mapContent/styles/modals'

export const WrapperModalSpace = styled(BaseWrapper).attrs(() => ({
  $flexDirection: 'column',
  $maxHeight: '70vh',
  $justifyLeftContent: 'center',
  $width: '100%',
}))`
  .top {
    display: flex;
    flex-direction: row;
    padding: ${({ theme }) => theme.padding(2)};
    background-color: ${({ theme }) => theme.colors.lightGrey};
    border-radius: 8px 8px 0 0;
    justify-content: space-between;
    width: 100%;

    .input-wrapper {
      flex: 0 0 auto;
      position: relative;
      border-radius: ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0 0;
      padding-right: 5px;
      width: 95%;
      flex-direction: column;

      @media (max-width: 715px) {
        width: 85%;
      }
    }

    .button-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      border-top-right-radius: ${({ theme }) => theme.borderRadius};
      position: relative;

      button {
        height: 40px;
        width: 40px;
        background-color: #fff;
        border: 1px solid #cccccc;
        border-radius: ${({ theme }) => theme.borderRadius};
        box-shadow: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0;
        transition: border-color 0.3s ease-in-out;
        position: fixed;

        &:hover {
          border: 1px solid #adadad;
        }
      }
    }
  }

  .tooltip {
    position: absolute;
    background-color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 14px;
    left: 10px;
    top: -75%;
    color: #fff;
    padding: 5px;
    border-radius: 5px;
    white-space: nowrap;
  }

  ${ChildrenOptions} {
    width: 100%;
    flex: 1;
    overflow-x: hidden;
    overflow-y: scroll;
    max-height: 50vh;

    ${ChildrenOption} {
      &.is-selected,
      &.is-selected:hover {
        background-color: ${({ theme }) => theme.colors.lightGrey};
      }
    }
  }
`

interface SpaceMainProps {
  layer: Layer
  setCurrentLayer: React.Dispatch<React.SetStateAction<Layer>>
  currentLayer: Layer
  isHighResolution?: boolean
}

export default function SpaceMain({
  layer,
  setCurrentLayer,
  currentLayer,
  isHighResolution = false,
}: SpaceMainProps) {
  const { context } = useAPI()
  const theme = useTheme()

  const [polygonDemarcation, setPolygonDemarcation] = useState<FeaturePolygonWithHash | null>(null)
  const [isOpenDemarcationModal, setIsOpenDemarcationModal] = useState<boolean>(false)

  useEffect(() => {
    setCurrentLayer(layer)
  }, [layer])

  const { callAction } = useActionFunction(geoListDefinition)

  const handleSelectChildrenOption = useCallback(
    (key: Exclude<SpaceType, 'custom'>) => {
      if (!context.user) {
        return
      }

      setCurrentLayer(oldLayer => {
        const space = {
          ...oldLayer.space,
          childrenType: key,
          childrenItemView: undefined,
          childrenH3Resolution: undefined,
        } as Layer['space']
        const analyzeResults = oldLayer.variable.result?.analyzeResults
        return {
          ...oldLayer,
          analyzeResults,
          space,
        }
      })
    },
    [context.user, setCurrentLayer],
  )

  const handleSelectChildrenViewOption = (key: 'item', childrenItemViewKey: string) => {
    if (!context.user) {
      return
    }

    setCurrentLayer(oldLayer => {
      const space = {
        ...oldLayer.space,
        childrenType: key,
        childrenItemView: childrenItemViewKey,
        childrenH3Resolution: undefined,
      } as Space

      const analyzeResults = oldLayer.variable.result?.analyzeResults
      return {
        ...oldLayer,
        analyzeResults,
        space,
      }
    })
  }

  const handleSetDelimitationFromSelect = useCallback(
    (
      item: {
        id: SpaceGeoStandardId
        name: string
        type: string
      } | null,
    ) => {
      if (!item) {
        return
      }
      if (!context.user) {
        return
      }

      setPolygonDemarcation(null)
      setCurrentLayer(oldLayer => {
        const newCompatibleChildren = (layer.variable as VariableAndOptionalResult)
          .compatibleChildrenTypes
          ? compatibleSpaceChildren[item.type as SpaceType].filter(type =>
              (layer.variable as VariableAndOptionalResult).compatibleChildrenTypes!.includes(type),
            )
          : compatibleSpaceChildren[item.type as SpaceType]

        let childrenType: SpaceType = oldLayer.space.childrenType
        let childrenTitle = (oldLayer.space as SpaceChildrenGeoStandard).childrenTitle
        let childrenItemView = (oldLayer.space as SpaceChildrenItem).childrenItemView
        if (!newCompatibleChildren.includes(childrenType)) {
          childrenType = newCompatibleChildren[0] as SpaceType

          if (childrenType === 'item') {
            childrenItemView = 'heatmap'
            childrenTitle = childrenItemViewLabels[childrenItemView]
          } else {
            childrenTitle = spaceTypePluralLabels[childrenType]
          }
        }

        const space = {
          ...oldLayer.space,
          childrenTitle,
          childrenItemView,
          childrenType,
          childrenH3Resolution: undefined,
          delimitationId: item.id,
          delimitationTitle: item.name,
          delimitationType: item.type,
        } as Space

        const analyzeResults = oldLayer.variable.result?.analyzeResults
        return {
          ...oldLayer,
          analyzeResults,
          space,
        }
      })
    },
    [context.user, layer.variable, setPolygonDemarcation, setCurrentLayer],
  )

  const handleSetDelimitationFromCustomPolygon = useCallback(
    (newPolygonDemarcation: FeaturePolygonWithHash | null) => {
      setPolygonDemarcation(newPolygonDemarcation)
      if (!newPolygonDemarcation) {
        return
      }

      setCurrentLayer(oldLayer => {
        let childrenType: SpaceType = 'item'
        let childrenItemView: 'cluster' | 'heatmap' =
          (oldLayer.space as SpaceChildrenItem).childrenItemView === 'cluster'
            ? 'cluster'
            : 'heatmap'
        let childrenTitle = childrenItemViewLabels[childrenItemView]

        const space: Space = {
          ...oldLayer.space,
          delimitationTitle: 'Demarcação por polígono',
          delimitationType: 'custom',
          delimitationCustom: newPolygonDemarcation,
          childrenType,
          childrenItemView,
          childrenTitle,
        }

        return {
          ...oldLayer,
          space,
        }
      })
    },
    [setPolygonDemarcation, setCurrentLayer],
  )

  const delimitationOptions = async (
    inputValue: string,
  ): Promise<
    Array<{
      id: SpaceGeoStandardId
      name: string
      type: SpaceGeoStandardType
    }>
  > => {
    const { resultPromise } = await callAction({
      query: inputValue.trim(),
      limit: 500,
      include: [],
      highResolution: isHighResolution,
    })
    const result = await resultPromise
    //Se houver ranking de buscas, realizar ordenação por ranking.
    if (result.items.length > 0 && result.items[0].searchRanking) {
      result.items.sort((a, b) => Number(b.searchRanking) - Number(a.searchRanking))
    }
    return result.items as {
      id: SpaceGeoStandardId
      name: string
      type: SpaceGeoStandardType
    }[]
  }

  const debouncedLoadOptions = useMemo(
    () => debounce(delimitationOptions, 500),
    [delimitationOptions, 500],
  )

  const currentCompatibleChildren = (layer.variable as VariableAndOptionalResult)
    .compatibleChildrenTypes
    ? compatibleSpaceChildren[currentLayer.space.delimitationType].filter(type =>
        (layer.variable as VariableAndOptionalResult).compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[currentLayer.space.delimitationType]

  const onResetDemarcation = () => {
    setPolygonDemarcation(null)
  }
  const formatOptionLabel = (item: {
    id: SpaceGeoStandardId
    name: string
    type: string
    subtitle?: string
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', margin: '0.2rem 0 0.2rem 0' }}>
      <span>{item?.name}</span>
      <span style={{ color: '#a5a4a4', fontSize: '0.8rem' }}>
        {spaceTypeSingularLabels[item?.type as SpaceType]}{' '}
        {item?.subtitle ? ' > ' + item?.subtitle : ''}
      </span>
    </div>
  )

  return (
    <WrapperModalSpace>
      <div className='top'>
        <div className='input-wrapper'>
          <AsyncSelect
            cacheOptions
            defaultOptions
            menuPlacement='auto'
            value={
              currentLayer.space.delimitationType !== 'custom'
                ? {
                    id: currentLayer.space.delimitationId,
                    name: currentLayer.space.delimitationTitle!,
                    type: currentLayer.space.delimitationType,
                  }
                : {
                    id: -1,
                    name: 'Demarcação customizada',
                    type: 'custom' as SpaceType,
                  }
            }
            loadOptions={debouncedLoadOptions}
            getOptionValue={item => String(item.id)}
            formatOptionLabel={formatOptionLabel}
            noOptionsMessage={() => 'Sem resultados'}
            loadingMessage={() => 'Carregando...'}
            placeholder='Delimitação'
            menuPortalTarget={document.body}
            onChange={handleSetDelimitationFromSelect}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              control: baseStyles => ({
                ...baseStyles,
                height: '40px',
                borderRadius: theme.borderRadius,
              }),
              menu: baseStyles => ({
                ...baseStyles,
                maxHeight: '200px',
                minHeight: 'auto',
              }),
              menuList: baseStyles => ({
                ...baseStyles,
                maxHeight: '200px',
                minHeight: 'auto',
              }),
            }}
          />
        </div>

        <div className='button-wrapper'>
          <button title='Demarcação customizada' onClick={() => setIsOpenDemarcationModal(true)}>
            <div style={{ marginTop: '4px' }}>
              <DemarcationIcon fill={'#0C2340'} width={22} height={22} />
            </div>
          </button>
        </div>
      </div>

      {isOpenDemarcationModal ? (
        <Modal isOpen={isOpenDemarcationModal} onClose={() => setIsOpenDemarcationModal(false)}>
          <ModalSpaceDemarcation
            onCloseModalDemarcation={() => setIsOpenDemarcationModal(false)}
            setPolygonDemarcation={handleSetDelimitationFromCustomPolygon}
            polygonDemarcation={polygonDemarcation}
          />
        </Modal>
      ) : null}

      <ChildrenOptions>
        {currentCompatibleChildren.map((key, index) => {
          const name =
            key !== currentLayer.space.delimitationType
              ? spaceTypePluralLabels[key]
              : spaceTypeSingularLabels[key]
          return (
            <Fragment key={index}>
              {(key as SpaceType) === 'item' ? (
                Object.keys(childrenItemViewLabels)
                  .filter(itemType => itemType !== 'arc')
                  .map((childrenItemViewLabelsKey, index) => {
                    return (
                      <React.Fragment key={index}>
                        <ChildrenOption
                          className={`${
                            (currentLayer.space as SpaceChildrenItem).childrenItemView ===
                            childrenItemViewLabelsKey
                              ? 'is-selected'
                              : ''
                          }`}
                          role='button'
                          onClick={() => {
                            handleSelectChildrenViewOption('item', childrenItemViewLabelsKey)
                          }}
                        >
                          <Left>
                            <Title>
                              {
                                childrenItemViewLabels[
                                  childrenItemViewLabelsKey as keyof typeof childrenItemViewLabels
                                ]
                              }
                            </Title>
                          </Left>
                        </ChildrenOption>
                      </React.Fragment>
                    )
                  })
              ) : (
                <ChildrenOption
                  key={index}
                  className={`${currentLayer.space.childrenType === key ? 'is-selected' : ''}`}
                  role='button'
                  onClick={() => {
                    handleSelectChildrenOption(key as Exclude<SpaceType, 'custom'>)
                    onResetDemarcation()
                  }}
                >
                  <Left>
                    <Title>{name}</Title>
                  </Left>
                </ChildrenOption>
              )}
            </Fragment>
          )
        })}
      </ChildrenOptions>
    </WrapperModalSpace>
  )
}
